// University of North Carolina at Chapel Hill: public college page (#2748),
// official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UNC-Chapel Hill's Common Data Set 2025-26
//    (CDS_UNCCH_2025-26_20260615.pdf, published June 15, 2026 by the Office of Institutional Research and
//    Assessment): C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table; C8 test policy for Fall 2027; C14/C16 dates; C21/C22
//    early plans. Quirk: the residency table is headed "Fall 2024", but its
//    rows sum exactly to the Fall 2025 C1 totals (76,247 applied, 12,751
//    admitted, 5,091 enrolled), so they are Fall 2025 counts. The CDS checks
//    the box saying final 2026-2027 costs were not yet available (expected
//    in August), so its G1/G5 figures are not used.
//  - admissions.unc.edu: Decisions (decision timing table), the FAQ on ACT
//    and SAT scores (the UNC System testing rule for 2026-2027 applicants and
//    beyond, scores listed on the application, superscoring) and the FAQ
//    on intended majors. The decision table carries no year; it is the page as posted on
//    2026-10-01, with the application open.
//  - UNC Scholarships and Student Aid, Cost of Attendance: the 26-27 in-state
//    and out-of-state budgets, living on campus.
//  - College Scorecard, school id 199120 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UNC-Chapel Hill Common Data Set 2025-2026',
        url: 'https://oira.unc.edu/wp-content/uploads/sites/297/2026/06/CDS_UNCCH_2025-26_20260615.pdf',
        read: '2026-10-01',
    },
    decisions: {
        label: 'UNC Undergraduate Admissions, Decisions',
        url: 'https://admissions.unc.edu/apply/decisions/',
        read: '2026-10-01',
    },
    testing: {
        label: 'UNC Undergraduate Admissions, FAQ: Is UNC test optional?',
        url: 'https://admissions.unc.edu/faqs/how-does-unc-look-at-act-or-sat-test-scores/',
        read: '2026-10-01',
    },
    majors: {
        label: 'UNC Undergraduate Admissions, FAQ: How does my intended major affect my application?',
        url: 'https://admissions.unc.edu/faqs/how-does-my-intended-major-affect-my-application/',
        read: '2026-10-01',
    },
    costs: {
        label: 'UNC Scholarships and Student Aid, Cost of Attendance',
        url: 'https://studentaid.unc.edu/cost-of-attendance/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?199120-University-of-North-Carolina-at-Chapel-Hill',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-north-carolina-at-chapel-hill',
    kbId: 'university_of_north_carolina_at_chapel_hill',
    name: 'University of North Carolina at Chapel Hill',
    shortName: 'UNC-Chapel Hill',
    aliases: ['UNC', 'UNC Chapel Hill'],
    location: 'Chapel Hill, NC',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UNC Kenan-Flagler brand color palette); decoration only.
    branding: {
        primary: '#4B9CD3',
        secondary: '#FFFFFF',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_north_carolina_at_chapel_hill_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 76247,
        admitted: 12751,
        enrolled: 5091,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 17846, admitted: 6605 },
            { group: 'Out-of-state', applied: 48645, admitted: 4685 },
            { group: 'International', applied: 9756, admitted: 1461 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UNC-Chapel Hill\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major. First-year students all start in the College of Arts and Sciences.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'UNC-Chapel Hill has no early decision plan. Its early action plan is nonbinding and not restrictive, and the Common Data Set does not report how many applied or were admitted in it, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to Carolina, not to a major, and the interests you list do not affect your admission. Some majors take a separate application later:',
        rows: [
            { school: 'Computer Science, Biomedical Engineering and the professional schools', requirement: 'Every first-year student starts in the College of Arts and Sciences. By the end of your second year you declare a major, or apply to the programs that require an application, including Computer Science, Biomedical Engineering and majors housed in the professional schools.' },
        ],
        source: 'majors',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'October 15', decision: 'By December 20 for North Carolina residents; by February 15 for all other applicants', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'March 31', binding: false },
        ],
        note: 'Admitted first-year students enroll by May 1. North Carolina residency must be confirmed through the Residency Determination Service.',
        source: 'decisions',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Under the UNC System rule for students applying for 2026-2027 and beyond, you do not need a score if your weighted GPA is 2.8 or above on a 4.0 scale. Below 2.8, you must send an ACT of 17 or higher or an SAT of 930 or higher. If you list scores on your application, UNC uses them in review and superscores across test dates; it can consider them if they arrive by November 7 for Early Action or February 15 for Regular Decision. If you enroll, official scores are required.',
        source: 'testing',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some fall 2027 applicants: those with a weighted GPA below 2.8.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [7230, 47472] },
            { item: 'Fees', amounts: [2130, 2130] },
            { item: 'Housing (on campus)', amounts: [9168, 9168] },
            { item: 'Food', amounts: [6702, 6702] },
            { item: 'Books and supplies', amounts: [638, 638] },
            { item: 'Travel', amounts: [1078, 2050] },
            { item: 'Loan fees', amounts: [56, 56] },
            { item: 'Personal expenses', amounts: [2048, 2048] },
        ],
        totals: [29050, 70264],
        note: 'For a full-time student living on campus. UNC calls these budgets average costs, used to set financial need; your own may differ.',
        source: 'costs',
        netPrice: {
            average: 11305,
            byIncome: [
                { income: '$0 to $30,000', amount: 2212 },
                { income: '$30,001 to $48,000', amount: 2989 },
                { income: '$48,001 to $75,000', amount: 6761 },
                { income: '$75,001 to $110,000', amount: 12883 },
                { income: 'Over $110,000', amount: 24276 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UNC-Chapel Hill\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
